import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, RefreshCw } from 'lucide-react';
import AssetLogo from './AssetLogo';
import PriceChange from './PriceChange';
import AIRecommendationBadge from './AIRecommendationBadge';
import StrategyMatchBar from './StrategyMatchBar';
import { useRealtimePrice } from '../hooks/useRealtimePrice';
import { useLazyStrategyBreakdown } from '../hooks/useLazyStrategyBreakdown';
import type { EnrichedSignal } from '../types/signals';

type SignalType = 'strong_buy' | 'buy' | 'neutral' | 'sell' | 'strong_sell';

interface SignalBadgeProps {
  signal: SignalType;
}

const SignalBadge: React.FC<SignalBadgeProps> = ({ signal }) => {
  const styles: Record<SignalType, string> = {
    strong_buy: 'bg-emerald-500/20 text-emerald-400',
    buy: 'bg-emerald-700/20 text-emerald-300',
    neutral: 'bg-slate-500/20 text-slate-400',
    sell: 'bg-red-700/20 text-red-300',
    strong_sell: 'bg-red-500/20 text-red-400',
  };
  const labels: Record<SignalType, string> = {
    strong_buy: '🟢 Strong Buy',
    buy: '🟢 Buy',
    neutral: '⚪ Hold',
    sell: '🔴 Sell',
    strong_sell: '🔴 Strong Sell',
  };
  return <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${styles[signal]}`}>{labels[signal]}</span>;
};

type TimingType = 'now' | 'next_candle' | 'watch';

interface TimingBadgeProps {
  timing?: TimingType;
}

const TimingBadge: React.FC<TimingBadgeProps> = ({ timing }) => {
  if (!timing) return null;

  const styles: Record<TimingType, string> = {
    now: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
    next_candle: 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
    watch: 'bg-slate-500/20 text-slate-400 border border-slate-500/30',
  };

  const labels: Record<TimingType, string> = {
    now: '⚡ NOW',
    next_candle: '⏳ Next Candle',
    watch: '👁️ Watch',
  };

  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap ${styles[timing]}`}>
      {labels[timing]}
    </span>
  );
};

const DayDropBadge: React.FC = () => (
  <span
    className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap bg-orange-500/20 text-orange-400 border border-orange-500/30"
    title="Technical signal overridden — severe intraday drop invalidates prior session setup"
  >
    ⚠️ Day drop
  </span>
);

interface ScreenerAssetRowProps {
  symbol: string;
  name: string;
  price: number;
  change: number;
  previousClose?: number;
  rsi: number;
  signal: SignalType;
  timing?: 'now' | 'next_candle' | 'watch';
  exchange: string;
  sector: string;
  currency: string;
  watchlist: string[];
  onToggleWatchlist: (symbol: string) => void;
  aiRecommendation?: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell';
  aiConfidence?: number;
  aiLoading?: boolean;
  onRequestAI: () => void;
  dayDropOverride?: boolean;
  showStrategyColumn?: boolean;
  showSignalColumn?: boolean;
  /** Score and breakdown context for v2 strategy-driven-signals users. */
  matchScore?: number;
  matchedStrategy?: string;
  strategiesMatched?: number;
  strategiesEvaluated?: number;
  /**
   * Full EnrichedSignal returned by the pre-compute pipeline (top-15 rows).
   * When present, no lazy fetch is triggered.
   */
  strategyBreakdown?: EnrichedSignal | null;
  /** Called when user clicks "Get Strategy" to trigger an on-demand evaluation. */
  onRequestStrategy?: () => void;
  /** True while the on-demand strategy request is in-flight. */
  strategyLoading?: boolean;
  showSignalValue?: boolean;
}

const ScreenerAssetRow: React.FC<ScreenerAssetRowProps> = ({
  symbol,
  name,
  price,
  change,
  previousClose,
  rsi,
  signal,
  timing,
  exchange,
  sector,
  currency,
  watchlist,
  onToggleWatchlist,
  aiRecommendation,
  aiConfidence,
  aiLoading = false,
  onRequestAI,
  dayDropOverride = false,
  showStrategyColumn = false,
  matchScore,
  matchedStrategy,
  strategiesMatched,
  strategiesEvaluated,
  strategyBreakdown,
  onRequestStrategy,
  strategyLoading = false,
  showSignalColumn = true,
  showSignalValue = true,
}) => {
  const navigate = useNavigate();
  // Only stream via WebSocket for FOREX/CC symbols; JSE/US/LSE are polled — skip per-row WS.
  const isStreaming = symbol.endsWith('.FOREX') || symbol.endsWith('.CC');
  const livePrice = useRealtimePrice(isStreaming ? symbol : '');

  // Lazy hover-evaluation disabled — on-demand "Get Strategy" button is used instead
  const isLazyRow = false;
  const { breakdown: lazyBreakdown, isLoading: lazyLoading, handleMouseEnter, handleMouseLeave } =
    useLazyStrategyBreakdown(symbol, isLazyRow);

  // Prefer pre-computed prop → lazy-fetched; fall back to flat props for backward compat
  const effectiveBreakdown: EnrichedSignal | null = strategyBreakdown ?? lazyBreakdown ?? null;
  const effectiveMatchScore = effectiveBreakdown?.match_score ?? matchScore;
  const effectiveStrategyName = effectiveBreakdown?.matched_strategy ?? matchedStrategy;
  const effectiveStrategiesMatched = effectiveBreakdown?.strategies_matched ?? strategiesMatched;
  const effectiveStrategiesEvaluated = effectiveBreakdown?.strategies_evaluated ?? strategiesEvaluated;

  // Show skeleton for lazy rows that have no data yet
  const showStrategyAsLoading = isLazyRow && !effectiveBreakdown;
  const hasStrategyData = effectiveBreakdown != null ||
    (effectiveMatchScore !== undefined && effectiveStrategyName !== undefined);

  // Debug logging for XAGUSD
  if (symbol === 'XAGUSD.FOREX') {
    console.log('[ScreenerAssetRow] XAGUSD Debug:', {
      symbol,
      isLive: livePrice.isLive,
      livePrice: livePrice.price,
      previousClose,
      staticChange: change,
    });
  }

  // Use live price if available, otherwise fall back to static price
  // Ensure we always have a valid number
  const displayPrice = livePrice.isLive && typeof livePrice.price === 'number'
    ? livePrice.price
    : (typeof price === 'number' ? price : 0);

  // Calculate change percentage from previous close for accuracy
  // This matches broker calculations (change from yesterday's close, not today's open)
  const displayChange = livePrice.isLive && typeof livePrice.price === 'number' && typeof previousClose === 'number'
    ? ((livePrice.price - previousClose) / previousClose) * 100
    : (typeof change === 'number' ? change : 0);

  return (
    <tr
      className="hover:bg-slate-700/30 cursor-pointer transition-colors group"
      onClick={() => navigate(`/asset/${symbol}`)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <td className="px-4 py-4">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleWatchlist(symbol);
          }}
          className={`transition-colors ${
            watchlist.includes(symbol)
              ? 'text-amber-400'
              : 'text-slate-600 group-hover:text-slate-400'
          }`}
        >
          <Star size={16} fill={watchlist.includes(symbol) ? 'currentColor' : 'none'} />
        </button>
      </td>
      <td className="px-4 py-4">
        <AssetLogo symbol={symbol} name={name} size="sm" />
      </td>
      <td className="px-4 py-4">
        <div className="flex items-center gap-2">
          <span className="font-mono font-semibold text-teal-400">{symbol.split('.')[0]}</span>
          {livePrice.isLive && (
            <span className="flex items-center gap-1 px-1.5 py-0.5 bg-emerald-500/10 border border-emerald-500/30 rounded text-[10px] font-semibold text-emerald-400 uppercase tracking-wide">
              <span className="w-1 h-1 bg-emerald-500 rounded-full animate-pulse" />
              LIVE
            </span>
          )}
        </div>
      </td>
      <td className="pl-4 pr-4 py-4 text-sm text-slate-300">{name}</td>
      <td className="px-2 py-4">
        <span className={`text-xs px-2 py-1 rounded ${
          exchange === 'JSE' ? 'bg-amber-500/20 text-amber-400' :
          exchange === 'Crypto' ? 'bg-purple-500/20 text-purple-400' :
          exchange === 'Commodities' ? 'bg-orange-500/20 text-orange-400' :
          'bg-blue-500/20 text-blue-400'
        }`}>
          {exchange}
        </span>
      </td>
      <td className="pl-2 pr-1 py-4 text-sm text-slate-400">
        <div className="truncate" title={sector}>{sector}</div>
      </td>
      <td className="pl-3 pr-1 py-4 text-right font-mono">
        {currency} {displayPrice.toFixed(2)}
      </td>
      <td className="pl-1 pr-2 py-4">
        <div className="flex justify-end">
          <PriceChange value={displayChange} size="sm" />
        </div>
      </td>
      <td className="px-2 py-4 text-right">
        <span
          className={`font-mono text-sm ${
            rsi < 30 ? 'text-emerald-400' : rsi > 70 ? 'text-red-400' : 'text-slate-300'
          }`}
        >
          {typeof rsi === 'number' ? rsi.toFixed(1) : '0.0'}
        </span>
      </td>
      {showSignalColumn && (
        <td className="px-2 py-4 text-center">
          {showSignalValue ? (
            <div className="flex flex-col items-center gap-1.5">
              <SignalBadge signal={signal} />
              <TimingBadge timing={timing} />
              {dayDropOverride && <DayDropBadge />}
            </div>
          ) : (
            <span className="px-2 py-1 rounded text-xs font-medium bg-slate-700/50 text-slate-300">
              Signal hidden
            </span>
          )}
        </td>
      )}
      {showStrategyColumn && (
        <td className="px-2 py-4">
          {(strategyLoading || showStrategyAsLoading || lazyLoading) ? (
            <StrategyMatchBar
              matchScore={0}
              strategyName=""
              strategiesMatched={0}
              strategiesEvaluated={0}
              isLoading
            />
          ) : hasStrategyData ? (
            <StrategyMatchBar
              matchScore={effectiveMatchScore ?? 0}
              strategyName={effectiveStrategyName ?? ''}
              strategiesMatched={effectiveStrategiesMatched ?? 0}
              strategiesEvaluated={effectiveStrategiesEvaluated ?? 0}
              isLoading={false}
            />
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRequestStrategy?.();
              }}
              className="px-2 py-1 text-xs text-slate-500 hover:text-teal-400 hover:bg-slate-700/50 rounded transition-colors"
              title="Evaluate strategies for this asset"
            >
              Get Strategy
            </button>
          )}
        </td>
      )}
      <td className="px-2 py-4 text-center">
        {aiLoading ? (
          <div className="flex justify-center">
            <RefreshCw size={16} className="animate-spin text-teal-400" />
          </div>
        ) : aiRecommendation ? (
          <div
            onClick={(e) => {
              e.stopPropagation();
              onRequestAI();
            }}
            className="cursor-pointer hover:opacity-80 transition-opacity flex flex-col items-center gap-1"
            title="Click to request new AI analysis"
          >
            {aiConfidence !== undefined && (
              <span className="text-[10px] text-slate-400 font-medium">
                {(aiConfidence * 100).toFixed(0)}%
              </span>
            )}
            <AIRecommendationBadge
              recommendation={aiRecommendation}
              confidence={aiConfidence}
              size="sm"
              showIcon={false}
            />
          </div>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRequestAI();
            }}
            className="px-2 py-1 text-xs text-slate-500 hover:text-teal-400 hover:bg-slate-700/50 rounded transition-colors"
            title="Request AI analysis"
          >
            Get AI
          </button>
        )}
      </td>
    </tr>
  );
};

export default ScreenerAssetRow;
